import React, { useState, useRef, useEffect } from "react";
import _ from "lodash";
import {
  EnvelopePlainOutlined,
  FrameInFrameOutlined,
  DeviceMobileOutlined,
} from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import {
  getPresetReminders,
  getScheduleTimeObject,
  getRemindersWithInitial,
} from "../modules/Utils";
import { getDateMomentObjectCustom } from "Utils/DateTimeHelpers";

const getScheduleOptions = ({
  initialScheduleTime,
  existingReminderTimes,
  t,
  isAllDayEvent = false,
}) => {
  const initialReminder = getScheduleTimeObject(
    initialScheduleTime,
    t,
    isAllDayEvent
  );
  const allOptions = getRemindersWithInitial(initialReminder, t, isAllDayEvent);

  // Filter out already selected schedule times, except for the current one
  return _.filter(allOptions, option => {
    // Keep the current reminder option
    if (isAllDayEvent) {
      return true;
    }
    if (initialReminder && option.value === initialReminder.value) {
      return true;
    }
    // Keep the custom option
    if (option.value === "custom") {
      return true;
    }
    // Filter out options that are already selected by other reminders
    return !_.includes(existingReminderTimes, option.value);
  });
};
/**
 * Finds an available time slot by adjusting the given time by ±15 minutes
 * @param {string} baseTime - Base time in HH:mm format
 * @param {Array} existingRemindersData - Array of existing reminders with scheduleTime and triggerTimeOffset
 * @param {number} currentScheduleTime - Current schedule time value
 * @returns {object} - Moment object with adjusted time
 */
const findAvailableTimeSlot = (
  baseTime,
  existingRemindersData,
  currentScheduleTime
) => {
  const usedTimes = _.chain(existingRemindersData)
    .filter(data => data.scheduleTime === currentScheduleTime)
    .map(data => data.triggerTimeOffset)
    .compact()
    .value();

  if (!_.includes(usedTimes, baseTime)) {
    return getDateMomentObjectCustom(baseTime, "HH:mm");
  }

  // Try adjusting by +15 or -15 minutes
  const baseMoment = getDateMomentObjectCustom(baseTime, "HH:mm");
  const adjustments = [15, -15, 30, -30, 45, -45]; // Try multiple increments

  for (const adjustment of adjustments) {
    const adjustedTime = baseMoment.clone().add(adjustment, "minutes");
    const timeString = adjustedTime.format("HH:mm");

    if (!_.includes(usedTimes, timeString)) {
      return adjustedTime;
    }
  }

  // If all slots are taken, return the base time with +15
  return baseMoment.clone().add(15, "minutes");
};

/**
 * Custom hook for reminder component logic
 */
export const useReminder = ({
  initialScheduleTime = null,
  onRemindersChange,
  onRemindersChangeMultiple,
  triggerTimeOffset,
  sentViaState: defaultSentViaState = [],
  existingReminderTimes = [], // Array of already selected schedule times to exclude from options
  existingRemindersData = [], // Array of existing reminders with full data for duplicate detection
  isAllDayEvent = false, // All-day event flag from eventModalV2 state
  triggerToastMsg,
  ...remProps
}) => {
  const { t } = useI18n(["calendar", "common"]);
  const PRESET_REMINDERS = getPresetReminders(t, isAllDayEvent);

  const [reminderScheduleOptions, setReminderScheduleOptions] = useState(() =>
    getScheduleOptions({
      initialScheduleTime,
      existingReminderTimes,
      t,
      isAllDayEvent,
    })
  );

  const [selectedReminderTime, setSelectedReminderTime] = useState(
    () =>
      getScheduleTimeObject(initialScheduleTime, t, isAllDayEvent) ||
      PRESET_REMINDERS[0]
  );

  const [allDayEventReminderTime, setAllDayEventReminderTime] = useState(
    triggerTimeOffset
      ? getDateMomentObjectCustom(triggerTimeOffset, "HH:mm")
      : getDateMomentObjectCustom("09:00", "HH:mm")
  );

  // Effect to update options when existing reminder times change
  useEffect(() => {
    // Filter out already selected schedule times, except for the current one
    const filteredOptions = getScheduleOptions({
      initialScheduleTime,
      existingReminderTimes,
      t,
      isAllDayEvent,
    });

    setReminderScheduleOptions(filteredOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingReminderTimes, initialScheduleTime, isAllDayEvent]);

  const currentPrefenceIdRef = useRef(null);
  const onCustomValueChange = (value = {}) => {
    const { days = 0, hours = 0, minutes = 0, weeks = 0 } = value;
    let optionLabel = "";
    let optionValue = 0;
    if (weeks) {
      optionLabel += `${t("calendar:count_weeks", { count: weeks })} `;
      optionValue += weeks * 7 * 24 * 60; // Convert weeks to minutes
    }
    if (days) {
      optionLabel += `${t("calendar:count_days", { count: days })} `;
      optionValue += days * 24 * 60; // Convert days to minutes
    }
    if (hours) {
      optionLabel += `${t("calendar:count_hours", { count: hours })} `;
      optionValue += hours * 60; // Convert hours to minutes
    }
    if (minutes) {
      optionLabel += `${t("calendar:count_minutes", { count: minutes })} `;
      optionValue += minutes;
    }

    const customReminderTime = {
      label: `${optionLabel} ${_.toLower(t("common:before"))}`,
      value: optionValue,
    };

    setReminderScheduleOptions(prevReminders => {
      // Filter out existing custom options and already selected times
      const filteredReminders = _.filter(prevReminders, option => {
        // Keep preset options that are not already selected elsewhere
        if (
          option.value !== "custom" &&
          !_.includes(existingReminderTimes, option.value)
        ) {
          return true;
        }
        // Keep the custom option
        return option.value === "custom";
      });

      return [...filteredReminders, customReminderTime];
    });

    setSelectedReminderTime(customReminderTime);

    if (onRemindersChange) {
      onRemindersChange(
        optionValue,
        currentPrefenceIdRef.current,
        "scheduleTime"
      );
    }
  };

  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const PRESET_SEND_VIA = [
    {
      value: "EMAIL",
      label: t("common:email"),
      prefixElement: <EnvelopePlainOutlined />,
    },
    {
      value: "IN_APP",
      label: t("calendar:in_app"),
      prefixElement: <FrameInFrameOutlined />,
    },
    {
      value: "PUSH",
      label: t("calendar:push_on_mobile"),
      prefixElement: <DeviceMobileOutlined />,
    },
  ];
  const [notificationPreferences, setNotificationPreferences] = useState(
    () => defaultSentViaState || _.map(PRESET_SEND_VIA, "value")
  );

  const notificationDropdownRef = useRef(null);

  // Initialize selected reminder based on initialReminder prop

  const handleReminderChange = (selectedOption, preferenceId) => {
    if (selectedOption?.value === "custom") {
      // Open custom modal for user to set a custom reminder
      setIsCustomModalOpen(true);
      currentPrefenceIdRef.current = preferenceId;
      return;
    } else if (selectedOption) {
      // Handle preset reminder selection

      // Auto-adjust time picker if duplicate scheduleTime detected (for all-day events)
      if (isAllDayEvent) {
        const adjustedTime = findAvailableTimeSlot(
          allDayEventReminderTime.format("HH:mm"),
          existingRemindersData,
          selectedOption?.value
        );

        const adjustedTimeString = adjustedTime.format("HH:mm");
        const isTimeAdjusted =
          adjustedTimeString !== allDayEventReminderTime.format("HH:mm");

        const updates = {
          scheduleTime: selectedOption?.value,
        };

        if (isTimeAdjusted) {
          updates.triggerTimeOffset = adjustedTimeString;
          setAllDayEventReminderTime(adjustedTime);
          triggerToastMsg(
            "calendar:you_cannot_set_two_reminders_at_the_same_time"
          );
        }

        onRemindersChangeMultiple(updates, preferenceId);
      } else {
        // For non-all-day events, just update scheduleTime
        if (onRemindersChange) {
          onRemindersChange(
            selectedOption?.value,
            preferenceId,
            "scheduleTime"
          );
        }
      }
    }
    setSelectedReminderTime(selectedOption);
  };

  const handleNotificationToggle = (value, preferenceId) => {
    setNotificationPreferences(value);
    if (onRemindersChange) onRemindersChange(value, preferenceId, "sentVia");
  };

  const handleReminderTimeChange = (value, preferenceId) => {
    onRemindersChange(value, preferenceId, "triggerTimeOffset");
  };

  /**
   * Handles time picker changes with duplicate detection
   * If the selected time matches another reminder with the same scheduleTime,
   * it will automatically adjust
   */
  const handleReminderTimeChangeWithDuplicateCheck = (
    momentValue,
    preferenceId
  ) => {
    const timeString = momentValue.format("HH:mm");
    const currentScheduleTimeValue = selectedReminderTime?.value;

    // Check if this time already exists for reminders with the same scheduleTime
    const isDuplicate = _.some(existingRemindersData, data => {
      return (
        data.scheduleTime === currentScheduleTimeValue &&
        data.triggerTimeOffset === timeString
      );
    });

    if (isDuplicate) {
      // Find an available time slot
      const adjustedTime = findAvailableTimeSlot(
        timeString,
        existingRemindersData,
        currentScheduleTimeValue
      );

      setAllDayEventReminderTime(adjustedTime);
      if (onRemindersChange) {
        onRemindersChange(
          adjustedTime.format("HH:mm"),
          preferenceId,
          "triggerTimeOffset"
        );
      }

      triggerToastMsg("calendar:you_cannot_set_two_reminders_at_the_same_time");
    } else {
      // No duplicate, proceed normally
      setAllDayEventReminderTime(momentValue);
      if (onRemindersChange) {
        onRemindersChange(timeString, preferenceId, "triggerTimeOffset");
      }
    }
  };

  return {
    selectedReminderTime,
    isCustomModalOpen,
    notificationPreferences,
    notificationDropdownRef,
    PRESET_REMINDERS,
    handleReminderChange,
    handleNotificationToggle,
    setIsCustomModalOpen,
    PRESET_SEND_VIA,
    reminderScheduleOptions,
    setReminderScheduleOptions,
    handleReminderTimeChange,
    handleReminderTimeChangeWithDuplicateCheck,
    allDayEventReminderTime,
    setAllDayEventReminderTime,
    t,
    onCustomValueChange,
    existingReminderTimes,
    ...remProps,
  };
};
